import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {BpmnConfigurationInterface} from '../interfaces/bpmn-configuration.interface';
import {
  bpmnAttachedResourcesTypeKey,
  bpmnConnectingResourcesApprovalStatusesKey,
  bpmnConnectingResourcesTypeKey,
  bpmnLinkedResourcesApprovalStatusesKey,
  bpmnLinkedResourcesTypeKey,
  bpmnTypeKey,
  clinicalTrialsSource,
  clinicalTrialStatusSearchParamsKey,
  curationItemApprovalStatusesKey,
  curationKeysPropertyName,
  curationStatusesKey,
  knowledgeBaseKey,
  overrideKey,
  targetStepForPolling,
  treeNavigationPolicy
} from '../models';
import {
  selectCurrentConfigPropertyValue,
  selectUiConfiguration
} from '../../../app/configuration/store/configuration.selector';
import {
  selectActiveJobInputValueByKey,
  selectActivePractitionerReferenceByType
} from '@ispm/workflow/store/workflow-config.selectors';
import {combineLatest, Observable} from 'rxjs';
import {selectCurationClinicalKeys, selectIsPathwaysSelectionActive} from '../store/selectors';
import {map} from "rxjs/operators";
import {get} from "lodash";
import {AuthenticationService} from '@ispm/user/services/authentication.service';
import {RouterManagerService} from "@ispm/core/services/router-manager.service";

@Injectable()
export class PathwaysConfigurationService implements BpmnConfigurationInterface {
  private DEFAULT_STATUSES = "approved,published";
  private workflowActionOverride$: Observable<any>;

  constructor(private store$: Store<any>,
              private authService: AuthenticationService,
              private routerManagerService: RouterManagerService) {
  }

  private getCurrentStepInputByKey(key: string): Observable<string> {
    return this.store$.pipe(
      select(selectActiveJobInputValueByKey(key))
    );
  }

  private getValueFromTestConfiguration(key: string) {
    return this.store$.pipe(
      select(selectCurrentConfigPropertyValue('Test', key))
    );
  }

  setUpHideResetButtonValue() {
    return this.workflowActionOverride$ = this.store$.pipe(
      select(selectUiConfiguration(null, 'Test', 'workflowAction')),
      map((workflowAction) => get(workflowAction, 'hideResetButton', false))
    );
  }

  getBpmnType() {
    return this.getValueFromTestConfiguration(bpmnTypeKey);
  }

  getTreeNavigationPolicy() {
    return this.getValueFromTestConfiguration(treeNavigationPolicy);
  }

  getBpmnLinkedResourcesType() {
    return this.getCurrentStepInputByKey(bpmnLinkedResourcesTypeKey);
  }

  getBpmnLinkedResourcesApprovalStatuses() {
    return this.getCurrentStepInputByKey(bpmnLinkedResourcesApprovalStatusesKey);
  }

  getBpmnConnectingResourcesType() {
    return this.getCurrentStepInputByKey(bpmnConnectingResourcesTypeKey);
  }

  getBpmnAttachedResourcesType() {
    return this.getCurrentStepInputByKey(bpmnAttachedResourcesTypeKey);
  }

  getKnowledgeBase() {
    return this.getValueFromTestConfiguration(knowledgeBaseKey);
  }

  getClinicalTrialStatusSearchParams() {
    return this.getValueFromTestConfiguration(clinicalTrialStatusSearchParamsKey);
  }

  getCurationStatusesConfiguration(key: string) {
    return combineLatest([
      this.getCurrentStepInputByKey(key + overrideKey),
      this.getValueFromTestConfiguration(curationStatusesKey).pipe(
        map((curationStatuses) =>  {
          if(curationStatuses) {
            return curationStatuses[key];
          }
          return this.DEFAULT_STATUSES;
        })
      )
    ]).pipe(
      map(([fromJobInput, fromTestConfig]) => fromJobInput || fromTestConfig)
    );
  }

  isPathwaySelectionEnabled() {
    const selectionActive$ = this.store$.pipe(select(selectIsPathwaysSelectionActive));
    const performer$ = this.store$.pipe(select(selectActivePractitionerReferenceByType('performer')));
    return combineLatest([selectionActive$, performer$]).pipe(
      map(([selectionActive, performer]) => selectionActive && performer === this.authService.user.info.id));
  }

  getBpmnConnectingResourcesApprovalStatuses() {
    return this.getCurationStatusesConfiguration(bpmnConnectingResourcesApprovalStatusesKey);
  }

  getCurationItemApprovalStatuses() {
    return this.getCurationStatusesConfiguration(curationItemApprovalStatusesKey);
  }

  getCurationClinicalKeysConfiguration() {
    return this.getValueFromTestConfiguration(curationKeysPropertyName);
  }

  // clinical trials can be from other organizations, default is from user organization
  getClinicalTrialsSources() {
    return this.getValueFromTestConfiguration(clinicalTrialsSource)
      .pipe(map((clinicalTrialsSource) => !!clinicalTrialsSource ? clinicalTrialsSource : this.authService.getUser().organization));
  }

  getCurationClinicalKeys() {
    return this.store$.pipe(select(selectCurationClinicalKeys));
  }

  getPatientId() {
    return this.routerManagerService.parameters.patientId;
  }

  getTargetStepForPolling() {
    return this.getCurrentStepInputByKey(targetStepForPolling);
  }

  getCurrentUserOrg(){
     return this.authService.getUser().organization;
  }

}
